import { APIError, NetworkError } from '../../types/api';

interface RetryConfig {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffFactor: number;
  retryableStatuses: number[];
}

const defaultConfig: RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffFactor: 2,
  retryableStatuses: [408, 429, 500, 502, 503, 504]
};

/**
 * Enhanced fetch client with retry mechanism
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @param retryConfig - Custom retry configuration
 */
export async function retryFetch<T>(
  url: string,
  options?: RequestInit,
  retryConfig: Partial<RetryConfig> = {}
): Promise<T> {
  const config = { ...defaultConfig, ...retryConfig };
  let lastError: Error;
  
  for (let attempt = 0; attempt < config.maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      
      if (!response.ok) {
        // Check if status is retryable
        if (config.retryableStatuses.includes(response.status)) {
          throw new APIError('Request failed', response.status);
        }
        throw new APIError(
          response.statusText || 'Request failed',
          response.status
        );
      }
      
      return await response.json();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      if (attempt === config.maxRetries - 1) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(
        config.initialDelay * Math.pow(config.backoffFactor, attempt),
        config.maxDelay
      );
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError instanceof APIError 
    ? lastError 
    : new NetworkError('Request failed after retries');
}